import React, { useCallback, useEffect, useState } from "react";
import { Panel } from "@/components/common/Panel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Copy, Check, QrCode, Link2, Sparkles, Mail, MessageCircle,
    Share2, Loader2, Globe, ShieldCheck, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useCapabilities } from "@/lib/capabilities";
import { ModuleGate } from "@/components/common/ModuleGate";
import { api } from "@/lib/api";
import QRCode from "qrcode";

// Production-facing canonical URL
const PUBLIC_BASE = process.env.REACT_APP_PUBLIC_URL || (typeof window !== "undefined" ? window.location.origin : "");

const VARIANTS = {
    tool: {
        slug: "tool",
        label: "Genesis Tool",
        path: "/",
        title: "UTSAVA Genesis · The Future of SAP Security Engineering",
        message:
            "Discover UTSAVA Genesis — an autonomous, explainable, offline SAP Security Engineering Platform. " +
            "Authorization · SU24 · OData inspection without ever touching a live SAP system. " +
            "Powered by ASSEP.",
    },
    admin: {
        slug: "admin",
        label: "Admin Console",
        path: "/admin",
        title: "UTSAVA Genesis · Admin Console",
        message:
            "Manage UTSAVA Genesis users, roles, and audit logs. Admin Console entry point. " +
            "Sign in to access (Admin role required).",
    },
};

const PLATFORMS = [
    {
        key: "linkedin", label: "LinkedIn", color: "#0A66C2",
        build: (url, text) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
        Icon: linkedInIcon,
    },
    {
        key: "twitter", label: "X (Twitter)", color: "#FFFFFF",
        build: (url, text) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
        Icon: xIcon,
    },
    {
        key: "facebook", label: "Facebook", color: "#1877F2",
        build: (url, text) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
        Icon: facebookIcon,
    },
    {
        key: "whatsapp", label: "WhatsApp", color: "#25D366",
        build: (url, text) => `https://api.whatsapp.com/send?text=${encodeURIComponent(text + " " + url)}`,
        Icon: whatsappIcon,
    },
    {
        key: "reddit", label: "Reddit", color: "#FF4500",
        build: (url, text) => `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`,
        Icon: redditIcon,
    },
    {
        key: "telegram", label: "Telegram", color: "#26A5E4",
        build: (url, text) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
        Icon: telegramIcon,
    },
    {
        key: "email", label: "Email", color: "#A5B4CF",
        build: (url, text) => `mailto:?subject=${encodeURIComponent("UTSAVA Genesis")}&body=${encodeURIComponent(text + "\n\n" + url)}`,
        Icon: () => <Mail size={16} />,
    },
];


export default function Share() {
    const { user } = useAuth();
    const { caps } = useCapabilities();
    const [variant, setVariant] = useState("tool");

    return (
        <ModuleGate module="share_hub" label="Share Hub">
        <div className="space-y-6 max-w-5xl mx-auto">
            <Panel
                eyebrow="Marketing · Distribution"
                title="Share UTSAVA Genesis"
            >
                <p className="text-sm text-slate-400 leading-relaxed max-w-3xl">
                    Get a masked, branded share link for the platform — ready to paste into LinkedIn, X,
                    WhatsApp, Reddit, Facebook, Telegram, or email. Each recipient lands on a polished
                    preview card (Open Graph) and the canonical UTSAVA URL. Use the Admin variant when
                    distributing to platform administrators only.
                </p>
            </Panel>

            <Tabs value={variant} onValueChange={setVariant} className="w-full">
                <TabsList className="bg-[var(--bg-surface-2)] border border-white/5 p-1 h-auto">
                    <TabsTrigger
                        value="tool"
                        data-testid="share-tab-tool"
                        className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-300 data-[state=active]:shadow-none text-slate-400 px-4 py-2 text-xs font-mono uppercase tracking-wider rounded-sm flex items-center gap-2"
                    >
                        <Sparkles size={12} /> Genesis Tool
                    </TabsTrigger>
                    <TabsTrigger
                        value="admin"
                        data-testid="share-tab-admin"
                        className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-300 data-[state=active]:shadow-none text-slate-400 px-4 py-2 text-xs font-mono uppercase tracking-wider rounded-sm flex items-center gap-2"
                    >
                        <ShieldCheck size={12} /> Admin Console
                    </TabsTrigger>
                </TabsList>

                {Object.values(VARIANTS).map((v) => (
                    <TabsContent key={v.slug} value={v.slug} className="mt-5">
                        <SharePanel variant={v} />
                    </TabsContent>
                ))}
            </Tabs>
        </div>
        </ModuleGate>
    );
}

function SharePanel({ variant }) {
    const fullUrl = `${PUBLIC_BASE.replace(/\/+$/, "")}${variant.path}`;
    const [shortUrl, setShortUrl] = useState("");
    const [shorting, setShorting] = useState(false);
    const [copied, setCopied] = useState(null);

    const shareUrl = shortUrl || fullUrl;
    // Rendered locally with the `qrcode` library — the platform never calls an
    // external QR service, and the PNG downloads as a real same-origin data URL.
    // Modules stay near-black on white so every scanner reads it first time; the
    // crimson framing lives on the surrounding card, not inside the code.
    const [qrSrc, setQrSrc] = useState("");
    useEffect(() => {
        let alive = true;
        QRCode.toDataURL(shareUrl, {
            width: 480, margin: 2, errorCorrectionLevel: "M",
            color: { dark: "#140205", light: "#FFFFFF" },
        })
            .then((url) => { if (alive) setQrSrc(url); })
            .catch(() => { if (alive) setQrSrc(""); });
        return () => { alive = false; };
    }, [shareUrl]);

    const fetchShort = useCallback(async () => {
        setShorting(true);
        try {
            // Branded, in-house short link on the platform's own domain (no third-party)
            const resp = await api.post("/share/links", {
                target_path: variant.path,
                label: variant.label,
            });
            const code = resp?.data?.code;
            if (code) {
                setShortUrl(`${PUBLIC_BASE.replace(/\/+$/, "")}/s/${code}`);
            } else {
                setShortUrl("");
            }
        } catch (e) {
            // graceful fallback — just use the full canonical URL
            console.error("Branded shortener failed; falling back to canonical URL", e);
            setShortUrl("");
        } finally { setShorting(false); }
    }, [variant.path, variant.label]);

    useEffect(() => {
        setShortUrl("");
        fetchShort();
    }, [fetchShort]);

    const copy = async (text, key) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(key);
            toast.success("Copied to clipboard");
            setTimeout(() => setCopied(null), 1600);
        } catch {
            toast.error("Clipboard unavailable");
        }
    };

    const shareNative = async () => {
        if (navigator.share) {
            try {
                await navigator.share({ title: variant.title, text: variant.message, url: shareUrl });
            } catch (e) {
                // user dismissed the native share sheet — non-fatal
                if (e?.name !== "AbortError") console.error("Native share failed", e);
            }
        } else {
            copy(`${variant.message} ${shareUrl}`, "native");
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
            <Panel eyebrow="Shareable Link" title={variant.title}>
                <div className="space-y-4">
                    <div>
                        <div className="label-eyebrow mb-2 flex items-center gap-1.5">
                            <Link2 size={11} /> Masked short URL
                        </div>
                        <div className="flex items-center gap-2">
                            <div data-testid="share-short-url" className="field flex-1 font-mono text-cyan-300 text-[13px] truncate">
                                {shorting ? "Generating…" : shortUrl || fullUrl}
                            </div>
                            <button
                                data-testid="share-copy-short"
                                onClick={() => copy(shareUrl, "short")}
                                className="btn-primary flex items-center gap-1.5"
                            >
                                {copied === "short" ? <Check size={12} /> : <Copy size={12} />}
                                {copied === "short" ? "Copied" : "Copy"}
                            </button>
                        </div>
                        {!shortUrl && !shorting && (
                            <button onClick={fetchShort} className="text-[11px] font-mono uppercase tracking-wider text-cyan-400 hover:text-cyan-300 mt-2">
                                Retry shortener
                            </button>
                        )}
                    </div>

                    <div>
                        <div className="label-eyebrow mb-2 flex items-center gap-1.5">
                            <Globe size={11} /> Canonical URL
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="field flex-1 font-mono text-slate-400 text-[12px] truncate">{fullUrl}</div>
                            <button onClick={() => copy(fullUrl, "full")} className="btn-ghost flex items-center gap-1.5">
                                {copied === "full" ? <Check size={12} /> : <Copy size={12} />}
                            </button>
                            <a href={fullUrl} target="_blank" rel="noreferrer" className="btn-ghost flex items-center gap-1.5">
                                <ExternalLink size={12} />
                            </a>
                        </div>
                    </div>

                    <div>
                        <div className="label-eyebrow mb-2">Pre-formatted message</div>
                        <textarea
                            data-testid="share-message"
                            readOnly
                            rows={3}
                            value={variant.message}
                            className="field font-mono text-[12px] text-slate-300"
                        />
                        <button onClick={() => copy(`${variant.message}\n\n${shareUrl}`, "msg")} className="btn-ghost mt-2 flex items-center gap-1.5">
                            {copied === "msg" ? <Check size={12} /> : <Copy size={12} />}
                            {copied === "msg" ? "Copied" : "Copy message + link"}
                        </button>
                    </div>

                    <div className="pt-2">
                        <div className="label-eyebrow mb-3 flex items-center gap-1.5">
                            <Share2 size={11} /> Direct share
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                            {PLATFORMS.map((p) => (
                                <a
                                    key={p.key}
                                    data-testid={`share-platform-${p.key}`}
                                    href={p.build(shareUrl, variant.message)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="panel px-3 py-2.5 flex items-center gap-2 hover:border-cyan-500/40 transition-colors group"
                                    style={{ borderLeft: `2px solid ${p.color}55` }}
                                >
                                    <span style={{ color: p.color }} className="shrink-0">
                                        <p.Icon />
                                    </span>
                                    <span className="text-sm text-slate-200 group-hover:text-white">{p.label}</span>
                                </a>
                            ))}
                            <button
                                onClick={shareNative}
                                className="panel px-3 py-2.5 flex items-center gap-2 hover:border-cyan-500/40 transition-colors text-cyan-300 col-span-2 sm:col-span-1"
                            >
                                <Share2 size={14} />
                                <span className="text-sm">Native share…</span>
                            </button>
                        </div>
                    </div>
                </div>
            </Panel>

            <Panel eyebrow="QR Code" title="Scan to open" testId="share-qr-panel">
                <div className="flex flex-col items-center">
                    <div className="relative p-3 bg-[var(--bg-base)] rounded-sm border border-cyan-500/30">
                        {qrSrc ? (
                            <img
                                data-testid="share-qr-img"
                                src={qrSrc}
                                alt={`QR code for ${shareUrl}`}
                                className="block"
                                width={240}
                                height={240}
                            />
                        ) : (
                            <div
                                data-testid="share-qr-loading"
                                className="w-[240px] h-[240px] flex items-center justify-center font-mono text-[10px] uppercase tracking-wider text-slate-400"
                            >
                                Rendering…
                            </div>
                        )}
                        <div className="absolute -inset-px rounded-sm pointer-events-none" style={{
                            boxShadow: "inset 0 0 0 1px rgb(var(--accent-1-rgb) / 0.18), 0 0 24px rgb(var(--accent-1-rgb) / 0.2)",
                        }} />
                    </div>
                    <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mt-4 text-center max-w-[240px]">
                        Point any camera · jumps to the masked link
                    </p>
                    <a
                        href={qrSrc}
                        download={`utsava-genesis-${variant.slug}-qr.png`}
                        className="btn-ghost mt-3 flex items-center gap-1.5"
                    >
                        <QrCode size={11} /> Download PNG
                    </a>
                </div>
            </Panel>
        </div>
    );
}

// --- Inline platform glyphs (use brand colours via parent text color) ---
function linkedInIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43A2.06 2.06 0 1 1 5.35 3.3a2.06 2.06 0 0 1-.01 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.99 0 1.78-.77 1.78-1.72V1.72C24 .77 23.21 0 22.22 0z" /></svg>
    );
}
function xIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
    );
}
function facebookIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9.198 21.5h4v-8.01h3.604l.396-3.98h-4V7.5a1 1 0 0 1 1-1h3v-4h-3a5 5 0 0 0-5 5v2.01h-2l-.396 3.98h2.396z" /></svg>
    );
}
function whatsappIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.52 3.48A11.94 11.94 0 0 0 12.05 0C5.46 0 .14 5.32.14 11.91a11.85 11.85 0 0 0 1.7 6.15L0 24l6.07-1.59a11.94 11.94 0 0 0 5.97 1.52h.01c6.59 0 11.91-5.32 11.91-11.92 0-3.18-1.24-6.18-3.44-8.53zM12.05 21.94h-.01a9.94 9.94 0 0 1-5.07-1.39l-.36-.21-3.6.94.96-3.51-.23-.36a9.93 9.93 0 0 1-1.53-5.31c0-5.49 4.47-9.95 9.95-9.95 2.66 0 5.16 1.04 7.04 2.92a9.85 9.85 0 0 1 2.92 7.04c0 5.49-4.47 9.94-9.95 9.94zm5.45-7.45c-.3-.15-1.77-.87-2.04-.97s-.47-.15-.67.15-.77.97-.95 1.17-.35.22-.65.07a8.15 8.15 0 0 1-2.4-1.48 9.04 9.04 0 0 1-1.66-2.07c-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5s.05-.37-.02-.52c-.07-.15-.67-1.62-.92-2.22-.24-.58-.48-.5-.67-.51l-.57-.01a1.1 1.1 0 0 0-.8.37 3.36 3.36 0 0 0-1.05 2.5c0 1.47 1.07 2.9 1.22 3.1.15.2 2.11 3.22 5.12 4.51.71.31 1.27.49 1.7.63.71.23 1.36.2 1.87.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.34z" /></svg>
    );
}
function redditIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm5.94 14.93a3 3 0 0 1-.27.91c-.93 2-3.85 3.43-7.27 3.43s-6.34-1.43-7.27-3.43a3 3 0 0 1-.27-.91 1.4 1.4 0 1 1 1.95-1.92c1.16-1.04 3.07-1.74 5.27-1.83l1.07-3.46.16-.38a.41.41 0 0 1 .43-.17l2.78.62a1.4 1.4 0 1 1 .12 1.04l-2.46-.55-.94 3.05a8.7 8.7 0 0 1 4.85 1.7 1.4 1.4 0 1 1 1.85 1.9zM9.5 14.2a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0zm6 0a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0zm-2.4 2.5a.4.4 0 0 1 .57-.1.4.4 0 0 1 .1.56C13.1 18 11.3 18.5 10 18.5s-3.1-.5-3.77-1.34a.4.4 0 0 1 .1-.56.4.4 0 0 1 .57.1c.46.6 1.93 1 3.1 1s2.64-.4 3.1-1z" /></svg>
    );
}
function telegramIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22.05 1.58 1.4 9.53c-1.4.55-1.4 1.34-.25 1.69l5.3 1.65 12.27-7.74c.58-.34 1.11-.16.68.22L9.46 14.34l-.38 5.49c.55 0 .79-.25 1.09-.55l2.62-2.55 5.46 4.03c1 .56 1.72.27 1.98-.93l3.59-16.92c.38-1.47-.55-2.13-1.78-1.33z" /></svg>
    );
}
